<script lang="ts">
  import { getApiError } from "../../../../../../api/error-handling"
  import { onDestroy, onMount, tick } from "svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { location } from "../../../../../../_shared/_logic/store"
  import { spaNavigate } from "../../../../../../_shared/_logic/navigation"
  import {
    fetchWorkOrderOverviewBootstrap,
    fetchWorkOrderTourSheetPdf,
  } from "../../../../../../api/kehrblatt/work-orders"
  import {
    registerEventCallback,
    unregisterEventCallback,
  } from "../../../../../_shared/_logic/eventbus"
  import { registerInvalidationHandler } from "@kontextwerk/web-sdk/core/runtime/invalidation"
  import WorkOrderFilter from "./_modules/work-order-filter"
  import type { FilterDisplayMode, WorkOrderFilterValue } from "./_shared/_logic/work-order-filters"
  import {
    createDefaultWorkOrderFilters,
    normalizeWorkOrderFilterValue,
  } from "./_shared/_logic/work-order-filters"
  import {
    readWorkOrdersTableUrlState,
    workOrdersTableUrlStateId,
    writeWorkOrdersTableUrlState,
  } from "./_logic/urlState"
  import { isActiveContactOrder } from "../../../../../../_shared/_logic/outreach-presentation"
  import { matchesOrganization, type AssistEventPayload } from "./_logic/assistEvents"
  import {
    buildBatchPlanningEmployeeContext,
    type BatchPlanningEmployeeContext,
  } from "./_shared/_logic/batch-planning-context"
  import { mergeById } from "./_shared/_logic/entities"
  import {
    invalidateWorkOrdersTable,
    workOrdersInvalidationTarget,
  } from "./_shared/_logic/invalidation"
  import WorkOrdersHeader from "./_modules/work-orders-header"
  import TourSheetPrintDialog from "./_modules/tour-sheet-print-dialog"
  import WorkOrdersTable from "./_modules/work-orders-table"
  import {
    buildWorkOrderTourSheetRequest,
    openTourSheetDownload,
    type TourSheetPrintOptions,
  } from "./_shared/_logic/tour-sheet"
  import type { OrgaViewFrameContext } from "../../../../context"
  import type { PageContext } from "../../navigation"

  const { viewFrameContext: _viewFrameContext, pageContext } = $props<{
    viewFrameContext: OrgaViewFrameContext
    pageContext: PageContext
  }>()

  const organization = $derived(pageContext.organization)
  const allowedManageEmployeeIds = $derived(pageContext.allowedManageEmployeeIds)
  const canEdit = $derived(pageContext.canManageScheduling)
  const subscriptionRestrictionActive = $derived(pageContext.subscriptionRestrictionActive)

  let workOrders = $state<WorkOrder[]>([])
  let properties = $state<Property[]>([])
  let customers = $state<Customer[]>([])
  let contactPoints = $state<ContactPoint[]>([])
  let orderTypes = $state<OrderType[]>([])
  let scheduleEvents = $state<ScheduleEvent[]>([])
  let contactOrders = $state<ContactOrder[]>([])
  let relevantScheduleEventByWorkOrderId = $state<Record<string, ScheduleEvent>>({})
  let metadataLoading = $state(true)
  let error = $state<string | null>(null)
  let planningSettings = $state<PlanningSettings | null>(null)
  let planningSettingsError = $state<string | null>(null)
  let filters = $state<WorkOrderFilterValue>(createDefaultWorkOrderFilters())
  let filterMode = $state<FilterDisplayMode>("compact")
  let filterError = $state<string | null>(null)
  let urlStateHydratedFor = $state<string | null>(null)
  let orderNumberSeedOrders = $state<WorkOrder[]>([])
  let metadataLoaded = $state(false)
  let loadPromise: Promise<void> | null = null
  let nextOrderNumberRemote = $state<string | null>(null)
  let totalCount = $state(0)
  let tourSheetPrinting = $state(false)
  let tourSheetPrintDialogOpen = $state(false)
  let tourSheetPrintError = $state<string | null>(null)
  let tourSheetObjectUrls: string[] = []
  const numberFormatter = new Intl.NumberFormat("de-DE")
  const titleWithCount = $derived.by(() => `Aufträge (${numberFormatter.format(totalCount)})`)
  const organizationSegment = $derived(organization?.slug ?? organization?.id ?? null)
  const currentUrlStateId = $derived(workOrdersTableUrlStateId(organization?.id ?? null))
  const batchUploadRequestKey = $derived.by(() => {
    const search = $location.search ?? ""

    if (!organization?.id || !search) return null

    try {
      const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search)

      return params.get("action") === "batch-upload" ? `${organization.id}:${search}` : null
    } catch {
      return null
    }
  })
  const resolveNextOrderNumber = (orders: WorkOrder[]) => {
    const numericOrderStrings = orders
      .map((order) => order.order_number?.trim() ?? "")
      .filter((value) => /^\d+$/.test(value))

    if (!numericOrderStrings.length) return null

    const maxLen = Math.max(...numericOrderStrings.map((value) => value.length))
    const maxVal = numericOrderStrings.reduce((acc, value) => {
      const num = Number(value)

      return Number.isFinite(num) && num > acc ? num : acc
    }, -1)

    if (maxVal < 0) return null

    const next = (maxVal + 1).toString()

    return next.padStart(maxLen, "0")
  }

  const viewEmployeeContext = $derived(
    buildBatchPlanningEmployeeContext(organization?.users ?? [], null),
  )
  const batchPlanningEmployeeContext = $derived(
    buildBatchPlanningEmployeeContext(organization?.users ?? [], allowedManageEmployeeIds),
  )
  const effectiveCanEdit = $derived(Boolean(canEdit && !subscriptionRestrictionActive))
  const effectiveCanPlan = $derived(
    Boolean(
      canEdit &&
      !subscriptionRestrictionActive &&
      (!Array.isArray(allowedManageEmployeeIds) || allowedManageEmployeeIds.length > 0) &&
      batchPlanningEmployeeContext.employees.length > 0,
    ),
  )
  const planningUnavailableReason = $derived.by(() => {
    if (!canEdit) return "Zum Planen fehlt die Berechtigung, Aufträge und Termine zu verwalten."

    if (subscriptionRestrictionActive) {
      return "Das Abonnement ist abgelaufen. Die Termin- und Sammelplanung ist deshalb nicht verfügbar."
    }

    if (Array.isArray(allowedManageEmployeeIds) && allowedManageEmployeeIds.length === 0) {
      return "Ihr Benutzer ist keinem aktiven, zulässigen Mitarbeiterbereich zugeordnet."
    }

    if (batchPlanningEmployeeContext.employees.length === 0) {
      return "Es ist kein aktiver, zulässiger Mitarbeiter verfügbar."
    }

    return null
  })
  const viewEmployees = $derived(viewEmployeeContext.employees)
  const planNowEmployees = $derived(batchPlanningEmployeeContext.employees)
  const workingHoursByUser = $derived(batchPlanningEmployeeContext.workingHoursByUser)
  const allEmployeeIds = $derived(batchPlanningEmployeeContext.allEmployeeIds)
  const activeContactOrderByWorkOrderId = $derived.by(() =>
    Object.fromEntries(
      contactOrders
        .filter((order) => order.work_order_id && isActiveContactOrder(order))
        .map((order) => [order.work_order_id, order]),
    ),
  )
  const nextOrderNumber = $derived(
    nextOrderNumberRemote ??
      resolveNextOrderNumber(orderNumberSeedOrders.length ? orderNumberSeedOrders : workOrders),
  )

  const handleProjectionEntitiesLoaded = (payload: {
    properties?: Property[] | null
    customers?: Customer[] | null
    contactPoints?: ContactPoint[] | null
    orderTypes?: OrderType[] | null
  }) => {
    properties = mergeById(properties, payload.properties)
    customers = mergeById(customers, payload.customers)
    contactPoints = mergeById(contactPoints, payload.contactPoints)
    orderTypes = mergeById(orderTypes, payload.orderTypes)
  }

  const handleFilterChange = (value: WorkOrderFilterValue) => {
    filters = normalizeWorkOrderFilterValue(value)
    filterError = null
    writeWorkOrdersTableUrlState(organization?.id ?? null, {
      filters,
      filterMode,
    })
  }

  const handleFilterReset = () => {
    filters = createDefaultWorkOrderFilters()
    writeWorkOrdersTableUrlState(organization?.id ?? null, {
      filters,
      filterMode,
    })
  }
  const handleFilterModeChange = (mode: FilterDisplayMode) => {
    filterMode = mode === "detailed" ? "detailed" : "compact"
    writeWorkOrdersTableUrlState(organization?.id ?? null, {
      filters,
      filterMode,
    })
  }
  const scheduleTourSheetObjectUrlCleanup = (objectUrl: string) => {
    tourSheetObjectUrls = [...tourSheetObjectUrls, objectUrl]
    window.setTimeout(() => {
      URL.revokeObjectURL(objectUrl)
      tourSheetObjectUrls = tourSheetObjectUrls.filter((value) => value !== objectUrl)
    }, 60_000)
  }

  const openPrintDialog = () => {
    if (tourSheetPrinting) return

    tourSheetPrintError = null
    tourSheetPrintDialogOpen = true
  }

  const handlePrint = async (options: TourSheetPrintOptions) => {
    const payload = buildWorkOrderTourSheetRequest({
      organizationId: organization?.id,
      ...options,
    })

    if (!payload || tourSheetPrinting) return

    tourSheetPrinting = true
    tourSheetPrintError = null
    try {
      const download = await fetchWorkOrderTourSheetPdf(payload)
      const objectUrl = openTourSheetDownload(download)

      if (objectUrl) scheduleTourSheetObjectUrlCleanup(objectUrl)

      tourSheetPrintDialogOpen = false
    } catch (error) {
      console.error("work_order_tour_sheet_pdf_failed")
      const reason = getApiError(error).reasonCode
      tourSheetPrintError =
        reason === "tour_sheet_entry_limit_exceeded"
          ? "Der Druck enthält mehr als 500 Einträge einschließlich zusätzlicher Termine. Bitte Zeitraum oder Mitarbeiter eingrenzen."
          : "Der Tourenzettel konnte nicht erstellt werden."
      newNotification({
        class: "error",
        html: tourSheetPrintError,
      })
    } finally {
      tourSheetPrinting = false
    }
  }
  const clearBatchUploadAction = () => {
    if (!organizationSegment) return

    spaNavigate(`/organizations/${encodeURIComponent(organizationSegment)}/work-orders`, {
      replace: true,
      noSearchParam: true,
    })
  }
  const runLoad = async () => {
    if (!organization?.id) {
      metadataLoaded = true
      metadataLoading = false
      nextOrderNumberRemote = null

      return
    }

    metadataLoading = true
    error = null
    try {
      const bootstrap = await fetchWorkOrderOverviewBootstrap(organization.id)
      orderTypes = bootstrap.order_types ?? []
      planningSettings = bootstrap.planning_settings ?? planningSettings
      planningSettingsError = null
      nextOrderNumberRemote = bootstrap.next_order_number ?? null
      metadataLoaded = true
    } catch (err) {
      console.error("work_orders_load_failed", err)
      error = "Aufträge konnten nicht geladen werden."
      planningSettingsError =
        planningSettingsError ?? "Planungseinstellungen konnten nicht geladen werden."
      metadataLoaded = true
    } finally {
      metadataLoading = false
    }
  }

  const loadMetadata = async () => {
    if (!loadPromise) {
      loadPromise = runLoad().finally(() => {
        loadPromise = null
      })
    }

    await loadPromise
  }

  const refreshTable = async () => {
    await tick()

    if (!organization?.id) return

    await invalidateWorkOrdersTable(organization.id)
  }

  const load = async () => {
    await loadMetadata()
    await refreshTable()
  }

  const ensureMetadataLoaded = async () => {
    if (metadataLoaded && !loadPromise) return

    await loadMetadata()
  }

  const resolveBatchPlanningEmployeeContext = async (): Promise<BatchPlanningEmployeeContext> => {
    if (planNowEmployees.length > 0) return batchPlanningEmployeeContext

    const refreshedOrganization = await _viewFrameContext.refreshOrganization?.()

    return buildBatchPlanningEmployeeContext(
      refreshedOrganization?.users ?? organization?.users ?? [],
      allowedManageEmployeeIds,
    )
  }

  onMount(() => {
    void loadMetadata()
  })

  let registeredInvalidationOrganizationId: string | null = null
  let unregisterWorkOrdersInvalidation: (() => void) | null = null
  $effect(() => {
    const organizationId = organization?.id ?? null

    if (organizationId === registeredInvalidationOrganizationId) return

    unregisterWorkOrdersInvalidation?.()
    unregisterWorkOrdersInvalidation = null
    registeredInvalidationOrganizationId = organizationId

    if (!organizationId) return

    unregisterWorkOrdersInvalidation = registerInvalidationHandler(
      workOrdersInvalidationTarget(organizationId),
      load,
    )
  })

  // effect-ok: url-state-hydration - route filter state is restored once the organization-scoped table id is known.
  $effect(() => {
    const id = currentUrlStateId

    if (!id || urlStateHydratedFor === id) return

    const state = readWorkOrdersTableUrlState(organization?.id ?? null)
    filters = state.filters
    filterMode = state.filterMode
    urlStateHydratedFor = id
  })

  let unregisterAssist: (() => void) | null = null
  onMount(() => {
    const handlerId = `work-orders-${organization?.id ?? "org"}`
    const handler = (payload: AssistEventPayload) => {
      if (!matchesOrganization(payload, organization?.id)) return

      void load()
    }
    registerEventCallback("assist.event", handlerId, handler)
    unregisterAssist = () => unregisterEventCallback("assist.event", handlerId)
  })
  onDestroy(() => {
    unregisterWorkOrdersInvalidation?.()
    unregisterAssist?.()
    for (const objectUrl of tourSheetObjectUrls) {
      URL.revokeObjectURL(objectUrl)
    }
    tourSheetObjectUrls = []
  })
</script>

<div class="work-orders-overview-view">
  <WorkOrdersHeader
    {organization}
    trialRestrictionsActive={_viewFrameContext.trialRestrictionsActive}
    canManageScheduling={canEdit}
    canPlanAppointments={effectiveCanPlan}
    {planningUnavailableReason}
    {properties}
    {customers}
    {contactPoints}
    {orderTypes}
    {planNowEmployees}
    {scheduleEvents}
    {planningSettings}
    {workingHoursByUser}
    {allEmployeeIds}
    {nextOrderNumber}
    title={titleWithCount}
    {filterMode}
    onFilterModeChange={handleFilterModeChange}
    onPrint={openPrintDialog}
    printPending={tourSheetPrinting}
    autoOpenBatchUploadKey={batchUploadRequestKey}
    onAutoBatchUploadConsumed={clearBatchUploadAction}
    {ensureMetadataLoaded}
    {resolveBatchPlanningEmployeeContext}
  />

  {#if tourSheetPrintDialogOpen}
    <TourSheetPrintDialog
      employees={viewEmployees}
      pending={tourSheetPrinting}
      error={tourSheetPrintError}
      onPrint={handlePrint}
      onClose={() => {
        tourSheetPrintDialogOpen = false
      }}
    />
  {/if}

  {#if planningSettingsError}
    <div class="error-state" style="margin-bottom: 0.5rem">
      <span>{planningSettingsError}</span>
    </div>
  {/if}
  <WorkOrderFilter
    bind:value={filters}
    {orderTypes}
    employees={viewEmployees}
    loading={metadataLoading}
    {filterMode}
    onChange={handleFilterChange}
    onReset={handleFilterReset}
  />
  {#if filterError}
    <div class="error-state filter-error">
      <span>{filterError}</span>
    </div>
  {/if}
  <WorkOrdersTable
    {organization}
    {workOrders}
    {filters}
    {orderTypes}
    {properties}
    {customers}
    {contactOrders}
    {scheduleEvents}
    {relevantScheduleEventByWorkOrderId}
    {activeContactOrderByWorkOrderId}
    {viewEmployees}
    {planNowEmployees}
    {workingHoursByUser}
    {allEmployeeIds}
    {planningSettings}
    loading={metadataLoading}
    onTotalChange={(value) => {
      totalCount = value
    }}
    onProjectionEntitiesLoaded={handleProjectionEntitiesLoaded}
    canEdit={effectiveCanEdit}
    canPlanAppointments={effectiveCanPlan}
    trialRestrictionsActive={_viewFrameContext.trialRestrictionsActive}
  />
</div>

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .work-orders-overview-view {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
  }

  .error-state {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;

    span {
      color: color-mix(in srgb, var(--text-invers-100) 70%, transparent);
    }
  }

  .filter-error {
    margin-top: 0.35rem;
  }
</style>
